import {
    AlertTriangle,
    ArrowRight,
    BriefcaseBusiness,
    CalendarClock,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    CircleAlert,
    Info,
    LoaderCircle,
    PlayCircle,
    RefreshCw,
    ShieldAlert,
    Sparkles,
    Trash2,
    Users,
    WalletCards,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FaArrowRightLong, FaCircleUser } from 'react-icons/fa6'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import StaffPickerModal from './StaffPickerModal'
import styles from './GeneratePayrollScreen.module.css'

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

const BLOCKING_WARNING_TYPES = new Set([
    'UNACCEPTED_ATTENDANCE',
    'NO_SALARY_ASSIGNMENT',
    'NO_ATTENDANCE_DATA',
])

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const padMonth = (value) => String(value).padStart(2, '0')

const getCycleKey = (year, month) => `${year}-${padMonth(month)}`

const shiftCycle = (year, month, step) => {
    const date = new Date(year, month - 1 + step, 1)
    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
    }
}

const formatCycleLabel = (year, month) => `${MONTH_NAMES[month - 1] || 'Month'} ${year}`

const formatINR = (amount) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

const normalizeStaffList = (payload) => {
    const candidates = [
        payload?.staff,
        payload?.staffReadiness,
        payload?.staffList,
        payload?.response,
    ]
    const staffArray = candidates.find(Array.isArray) || []

    return staffArray.map((item, index) => ({
        staffIdentifier: item?.staffIdentifier || item?.employeeIdentifier || item?.id || `staff-${index}`,
        staffName: item?.staffName || item?.name || item?.fullName || 'Unnamed Staff',
        staffCode: item?.staffCode || item?.employeeCode || '',
        designationLabel: item?.designationLabel || item?.designation || '',
    }))
}

const getWarningTone = (warningType) => {
    return BLOCKING_WARNING_TYPES.has(String(warningType || '').toUpperCase()) ? 'blocking' : 'info'
}

const GeneratePayrollScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { runIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const latestRun = location?.state?.latestRun || null
    const now = new Date()

    const [cycleYear, setCycleYear] = useState(now.getFullYear())
    const [cycleMonth, setCycleMonth] = useState(now.getMonth() + 1)
    const [excludedStaff, setExcludedStaff] = useState([])
    const [warnings, setWarnings] = useState([])
    const [requiresAck, setRequiresAck] = useState(false)
    const [isGenerating, setIsGenerating] = useState(false)
    const [result, setResult] = useState(null)
    const [acknowledged, setAcknowledged] = useState(false)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [pendingExcludedStaff, setPendingExcludedStaff] = useState(null)
    const [pendingExcludeReason, setPendingExcludeReason] = useState('')
    const [excludeReasonError, setExcludeReasonError] = useState('')
    const [scopeData, setScopeData] = useState({ totalActiveStaff: 0, staff: [] })
    const [isScopeLoading, setIsScopeLoading] = useState(true)
    const [scopeError, setScopeError] = useState('')
    const [runConflict, setRunConflict] = useState(null)
    const [lockedFinalizedCycleKey, setLockedFinalizedCycleKey] = useState('')

    const cycleLabel = useMemo(() => formatCycleLabel(cycleYear, cycleMonth), [cycleMonth, cycleYear])
    const cycleKey = useMemo(() => getCycleKey(cycleYear, cycleMonth), [cycleMonth, cycleYear])
    const isLockedFinalizedCycle = lockedFinalizedCycleKey === cycleKey

    const effectiveIncludedCount = useMemo(() => {
        return Math.max(0, Number(scopeData?.totalActiveStaff || 0) - excludedStaff.length)
    }, [excludedStaff.length, scopeData?.totalActiveStaff])

    const displayResult = result || (location?.state?.highlightGenerated ? latestRun : null)
    const resultWarnings = displayResult?.warnings || []
    const currentRunSnapshot =
        displayResult ||
        runConflict ||
        latestRun ||
        (runIdentifier ? { runIdentifier, cycleYear, cycleMonth, cycleLabel } : null)

    const fetchStaffScope = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            setScopeError('Please select a store before generating payroll.')
            setScopeData({ totalActiveStaff: 0, staff: [] })
            setIsScopeLoading(false)
            return
        }

        try {
            setIsScopeLoading(true)
            setScopeError('')

            const response = await fetch(BASE_URL_API + 'getPayrollStaffReadiness', {
                method: 'POST',
                cache: 'no-store',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                }),
            })

            if (!response.ok) {
                throw new Error('Server error')
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const payload = respo?.response || {}
                const staff = normalizeStaffList(payload)
                setScopeData({
                    totalActiveStaff: Number(payload?.totalActiveStaff ?? staff.length),
                    staff,
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            setScopeError(respo?.message || 'Unable to load active staff scope.')
        } catch {
            setScopeError('Unable to load active staff scope right now.')
        } finally {
            setIsScopeLoading(false)
        }
    }, [activeStore?.storeIdentifier, authCtx])

    useEffect(() => {
        fetchStaffScope()
    }, [fetchStaffScope])

    useEffect(() => {
        setWarnings([])
        setRequiresAck(false)
        setAcknowledged(false)
        setResult(null)
        setRunConflict(null)
    }, [cycleKey, excludedStaff])

    const moveCycle = (step) => {
        const nextCycle = shiftCycle(cycleYear, cycleMonth, step)
        setCycleYear(nextCycle.year)
        setCycleMonth(nextCycle.month)
    }

    const handlePickStaff = (staff) => {
        const alreadyExcluded = excludedStaff.some(
            (item) => item.staffIdentifier === staff?.staffIdentifier
        )

        if (alreadyExcluded) {
            toast.info(`${staff?.staffName || 'This staff member'} is already excluded.`)
            return
        }

        setPickerOpen(false)
        setPendingExcludedStaff(staff)
        setPendingExcludeReason('')
        setExcludeReasonError('')
    }

    const addExclusion = () => {
        if (!pendingExcludedStaff?.staffIdentifier) return

        if (!pendingExcludeReason.trim()) {
            setExcludeReasonError('Add a reason before excluding this staff member.')
            return
        }

        setExcludedStaff((prev) => [
            ...prev,
            {
                staffIdentifier: pendingExcludedStaff.staffIdentifier,
                staffName: pendingExcludedStaff.staffName,
                reason: pendingExcludeReason.trim(),
            },
        ])
        setPendingExcludedStaff(null)
        setPendingExcludeReason('')
        setExcludeReasonError('')
    }

    const removeExcludedStaff = (staffIdentifier) => {
        setExcludedStaff((prev) => prev.filter((item) => item.staffIdentifier !== staffIdentifier))
    }

    const generatePayroll = async (acknowledgeWarnings = false) => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            toast.error('Please select a store before generating payroll.')
            return
        }

        if (isLockedFinalizedCycle) {
            toast.info('This cycle already has a finalized payroll run. Select another month.')
            return
        }

        try {
            setIsGenerating(true)

            const response = await fetch(BASE_URL_API + 'generatePayrollRun', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    cycleYear,
                    cycleMonth,
                    acknowledgeWarnings,
                    excludeStaffIdentifiers: excludedStaff.map((staff) => staff.staffIdentifier),
                    excludeReasons: excludedStaff.map((staff) => staff.reason),
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setResult(respo.response)
                setWarnings(respo?.response?.warnings || [])
                setRequiresAck(false)
                setAcknowledged(false)
                toast.success(respo?.message || `${cycleLabel} payroll generated successfully.`)
                navigate(`/payroll/run/${respo.response.runIdentifier}`, {
                    replace: true,
                    state: {
                        latestRun: respo.response,
                        highlightGenerated: true,
                    },
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'PAY_006':
                    setWarnings(respo?.response?.warnings || [])
                    setRequiresAck(true)
                    setAcknowledged(false)
                    toast.warning(respo?.message || 'Issues found. Review and acknowledge to continue.')
                    break
                case 'PAY_004': {
                    const conflictData = {
                        ...respo?.response,
                        cycleYear,
                        cycleMonth,
                        cycleLabel,
                    }
                    setRunConflict(conflictData)

                    if (String(respo?.response?.runStatus || '').toUpperCase() === 'FINALIZED') {
                        setLockedFinalizedCycleKey(cycleKey)
                    }

                    toast.warning(
                        `A ${cycleLabel} run already exists (${respo?.response?.runStatus || 'UNKNOWN'}).`
                    )
                    if (respo?.response?.existingRunIdentifier) {
                        navigate(`/payroll/run/${respo.response.existingRunIdentifier}`, {
                            replace: true,
                            state: {
                                latestRun: conflictData,
                                existingRun: true,
                                existingRunMessage: respo?.message || '',
                            },
                        })
                    }
                    break
                }
                case 'SETUP_001':
                case 'SETUP_002':
                    toast.error(respo?.message || 'Complete payroll setup first.')
                    navigate('/payroll/setup')
                    break
                case 'PAY_008':
                    toast.info(respo?.message || 'No staff available to process for this cycle.')
                    break
                case 'SYS_002':
                    toast.error(respo?.message || 'Unable to generate payroll right now.')
                    break
                default:
                    toast.error(respo?.message || 'Failed to generate payroll.')
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setIsGenerating(false)
        }
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} type="button" onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <PlayCircle size={12} />
                        Payroll Run
                    </span>
                    <h1 className={styles.headerTitle}>Generate Payroll</h1>
                    <p className={styles.headerSub}>
                        Create a fresh draft payroll run for {cycleLabel}, review warnings, and
                        exclude edge-case staff before processing.
                    </p>
                </div>
            </div>

            {currentRunSnapshot ? (
                <section className={styles.snapshotCard}>
                    <div className={styles.snapshotHead}>
                        <Sparkles size={15} />
                        <span>{result ? 'Latest Generated Run' : 'Existing Run Snapshot'}</span>
                    </div>
                    <div className={styles.snapshotGrid}>
                        <div className={styles.snapshotCell}>
                            <span>Cycle</span>
                            <strong>
                                {currentRunSnapshot?.cycleLabel ||
                                    formatCycleLabel(
                                        Number(currentRunSnapshot?.cycleYear || cycleYear),
                                        Number(currentRunSnapshot?.cycleMonth || cycleMonth)
                                    )}
                            </strong>
                        </div>
                        <div className={styles.snapshotCell}>
                            <span>Status</span>
                            <strong>{currentRunSnapshot?.runStatus || 'DRAFT'}</strong>
                        </div>
                        <div className={styles.snapshotCell}>
                            <span>Run ID</span>
                            <strong>{currentRunSnapshot?.runIdentifier || currentRunSnapshot?.existingRunIdentifier || '—'}</strong>
                        </div>
                        <div className={styles.snapshotCell}>
                            <span>Staff Count</span>
                            <strong>{Number(currentRunSnapshot?.totalStaffCount || 0)}</strong>
                        </div>
                    </div>
                    {!displayResult && (runConflict || location?.state?.existingRun || runIdentifier) ? (
                        <p className={styles.snapshotNote}>
                            {location?.state?.existingRunMessage ||
                                'This cycle already has a run. A dedicated payroll run detail screen is not present yet, so the generator stays on this page and shows the available snapshot.'}
                        </p>
                    ) : null}
                </section>
            ) : null}

            <section className={styles.cycleCard}>
                <div className={styles.cardHead}>
                    <div>
                        <p className={styles.cardEyebrow}>Cycle Selector</p>
                        <h2>Choose the payroll month</h2>
                    </div>
                    <button
                        type="button"
                        className={styles.refreshBtn}
                        onClick={fetchStaffScope}
                        disabled={isScopeLoading}
                    >
                        {isScopeLoading ? (
                            <LoaderCircle size={14} className={styles.spinIcon} />
                        ) : (
                            <RefreshCw size={14} />
                        )}
                        Refresh Scope
                    </button>
                </div>

                <div className={styles.cyclePicker}>
                    <button type="button" className={styles.cycleArrowBtn} onClick={() => moveCycle(-1)}>
                        <ChevronLeft size={16} />
                    </button>
                    <div className={styles.cycleDisplay}>
                        <span className={styles.cycleBadge}>
                            <CalendarClock size={14} />
                            {cycleLabel}
                        </span>
                        <p>
                            Payroll runs are always generated in <strong>DRAFT</strong> mode first
                            so they remain recomputable without side effects.
                        </p>
                    </div>
                    <button type="button" className={styles.cycleArrowBtn} onClick={() => moveCycle(1)}>
                        <ChevronRight size={16} />
                    </button>
                </div>

                {isLockedFinalizedCycle ? (
                    <div className={`${styles.inlineAlert} ${styles.inlineAlertWarn}`}>
                        <ShieldAlert size={15} />
                        <span>
                            {cycleLabel} already has a <strong>FINALIZED</strong> payroll run and
                            is locked for regeneration.
                        </span>
                    </div>
                ) : null}
            </section>

            <section className={styles.scopeCard}>
                <div className={styles.cardHead}>
                    <div>
                        <p className={styles.cardEyebrow}>Staff Scope</p>
                        <h2>Who gets included in this run?</h2>
                    </div>
                    <span className={styles.scopePill}>
                        <Users size={14} />
                        {isScopeLoading ? 'Loading…' : `${effectiveIncludedCount} planned to process`}
                    </span>
                </div>

                {scopeError ? (
                    <div className={`${styles.inlineAlert} ${styles.inlineAlertWarn}`}>
                        <CircleAlert size={15} />
                        <span>{scopeError}</span>
                    </div>
                ) : (
                    <div className={styles.scopeGrid}>
                        <div className={styles.scopeSummaryCard}>
                            <Users size={18} />
                            <strong>{Number(scopeData?.totalActiveStaff || 0)}</strong>
                            <span>active staff included by default</span>
                        </div>
                        <div className={styles.scopeSummaryCard}>
                            <Trash2 size={18} />
                            <strong>{excludedStaff.length}</strong>
                            <span>manual exclusions for this draft</span>
                        </div>
                        <div className={styles.scopeSummaryCard}>
                            <BriefcaseBusiness size={18} />
                            <strong>Auto-handled</strong>
                            <span>FnF-settled staff are excluded by the payroll engine</span>
                        </div>
                    </div>
                )}
            </section>

            <section className={styles.exclusionsCard}>
                <div className={styles.cardHead}>
                    <div>
                        <p className={styles.cardEyebrow}>Manual Exclusions</p>
                        <h2>Skip edge-case staff for this cycle</h2>
                    </div>
                    <button type="button" className={styles.addBtn} onClick={() => setPickerOpen(true)}>
                        + Exclude a Staff Member
                    </button>
                </div>

                <p className={styles.sectionHint}>
                    Use this only for exceptional cases like disputed attendance. Each exclusion
                    requires an audit reason.
                </p>

                {pendingExcludedStaff ? (
                    <div className={styles.pendingExclusionCard}>
                        <div className={styles.pendingExclusionHead}>
                            <div className={styles.pendingIdentity}>
                                <FaCircleUser className={styles.avatarIcon} />
                                <div>
                                    <strong>{pendingExcludedStaff.staffName}</strong>
                                    <span>
                                        {pendingExcludedStaff.designationLabel || 'Staff'}
                                        {pendingExcludedStaff.staffCode ? ` • ${pendingExcludedStaff.staffCode}` : ''}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className={styles.inlineGhostBtn}
                                onClick={() => {
                                    setPendingExcludedStaff(null)
                                    setPendingExcludeReason('')
                                    setExcludeReasonError('')
                                }}
                            >
                                Cancel
                            </button>
                        </div>

                        <textarea
                            className={`${styles.textArea} ${excludeReasonError ? styles.inputError : ''}`}
                            rows={3}
                            value={pendingExcludeReason}
                            onChange={(event) => {
                                setPendingExcludeReason(event.target.value)
                                if (excludeReasonError) setExcludeReasonError('')
                            }}
                            placeholder="e.g. Attendance under dispute — will process next month"
                        />
                        {excludeReasonError ? (
                            <span className={styles.errorText}>{excludeReasonError}</span>
                        ) : null}

                        <div className={styles.pendingActionRow}>
                            <button type="button" className={styles.primaryInlineBtn} onClick={addExclusion}>
                                Save Exclusion <FaArrowRightLong size={12} />
                            </button>
                        </div>
                    </div>
                ) : null}

                {excludedStaff.length === 0 ? (
                    <div className={styles.emptyMiniState}>
                        <Info size={15} />
                        <span>No manual exclusions added yet.</span>
                    </div>
                ) : (
                    <div className={styles.exclusionList}>
                        {excludedStaff.map((staff) => (
                            <div key={staff.staffIdentifier} className={styles.exclusionRow}>
                                <div className={styles.exclusionIdentity}>
                                    <FaCircleUser className={styles.avatarIcon} />
                                    <div>
                                        <strong>{staff.staffName}</strong>
                                        <span>{staff.reason}</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={styles.removeBtn}
                                    onClick={() => removeExcludedStaff(staff.staffIdentifier)}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {requiresAck ? (
                <section className={styles.warningCard}>
                    <div className={styles.cardHead}>
                        <div>
                            <p className={styles.cardEyebrow}>Warning Gate</p>
                            <h2>Issues found — review before proceeding</h2>
                        </div>
                        <span className={styles.warningPill}>
                            <AlertTriangle size={14} />
                            {warnings.length} issue(s)
                        </span>
                    </div>

                    <div className={styles.warningList}>
                        {warnings.map((warning, index) => {
                            const tone = getWarningTone(warning?.type)

                            return (
                                <div
                                    key={`${warning?.staffIdentifier || index}-${warning?.type || 'warning'}`}
                                    className={`${styles.warningRow} ${
                                        tone === 'blocking' ? styles.warningRowBlocking : styles.warningRowInfo
                                    }`}
                                >
                                    <div className={styles.warningMeta}>
                                        <strong>{warning?.staffName || 'Staff issue'}</strong>
                                        <span>{warning?.message || 'Please review this warning.'}</span>
                                    </div>
                                    <span className={styles.warningType}>{warning?.type || 'WARNING'}</span>
                                </div>
                            )
                        })}
                    </div>

                    <label className={styles.checkboxRow}>
                        <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(event) => setAcknowledged(event.target.checked)}
                        />
                        <span>I acknowledge these issues and want to proceed with draft generation.</span>
                    </label>
                </section>
            ) : null}

            <section className={styles.actionCard}>
                <div>
                    <p className={styles.cardEyebrow}>Generate</p>
                    <h2>{`Generate ${cycleLabel} payroll`}</h2>
                    <p className={styles.sectionHint}>
                        Draft mode computes earnings, deductions, PF/ESI, exclusions, and warning
                        metadata. Final side effects stay deferred until finalization.
                    </p>
                </div>

                <button
                    type="button"
                    className={styles.generateBtn}
                    onClick={() => generatePayroll(requiresAck)}
                    disabled={isGenerating || (requiresAck && !acknowledged) || isLockedFinalizedCycle}
                >
                    {isGenerating ? (
                        <>
                            <LoaderCircle size={15} className={styles.spinIcon} />
                            Generating...
                        </>
                    ) : (
                        <>
                            {requiresAck ? `Acknowledge & Generate ${cycleLabel}` : `Generate ${cycleLabel} Payroll`}
                            <ArrowRight size={14} />
                        </>
                    )}
                </button>
            </section>

            {displayResult ? (
                <section className={styles.resultCard}>
                    <div className={styles.cardHead}>
                        <div>
                            <p className={styles.cardEyebrow}>Run Summary</p>
                            <h2>{displayResult?.cycleLabel || cycleLabel}</h2>
                        </div>
                        <span className={styles.resultStatusPill}>
                            <CheckCircle2 size={14} />
                            {displayResult?.runStatus || 'DRAFT'}
                        </span>
                    </div>

                    <div className={styles.resultMetricGrid}>
                        <div className={styles.metricCard}>
                            <WalletCards size={16} />
                            <strong>₹{formatINR(displayResult?.totalNetPayout)}</strong>
                            <span>Net payout</span>
                        </div>
                        <div className={styles.metricCard}>
                            <Users size={16} />
                            <strong>{Number(displayResult?.totalStaffCount || 0)}</strong>
                            <span>Processed staff</span>
                        </div>
                        <div className={styles.metricCard}>
                            <AlertTriangle size={16} />
                            <strong>{Number(displayResult?.totalExcludedCount || 0)}</strong>
                            <span>Excluded staff</span>
                        </div>
                        <div className={styles.metricCard}>
                            <CalendarClock size={16} />
                            <strong>{Number(displayResult?.pendingAttendanceCount || 0)}</strong>
                            <span>Pending attendance cases</span>
                        </div>
                    </div>

                    <div className={styles.financialGrid}>
                        <div className={styles.financialCell}>
                            <span>Gross Earnings</span>
                            <strong>₹{formatINR(displayResult?.totalGrossEarnings)}</strong>
                        </div>
                        <div className={styles.financialCell}>
                            <span>Total Deductions</span>
                            <strong>₹{formatINR(displayResult?.totalDeductions)}</strong>
                        </div>
                        <div className={styles.financialCell}>
                            <span>Employer PF</span>
                            <strong>₹{formatINR(displayResult?.totalEmployerPf)}</strong>
                        </div>
                        <div className={styles.financialCell}>
                            <span>Employer ESI</span>
                            <strong>₹{formatINR(displayResult?.totalEmployerEsi)}</strong>
                        </div>
                    </div>

                    <div className={styles.resultSection}>
                        <div className={styles.resultSectionHead}>
                            <h3>Included Staff</h3>
                            <span>{(displayResult?.staffSummaries || []).length} entries</span>
                        </div>
                        <div className={styles.resultList}>
                            {(displayResult?.staffSummaries || []).map((staff) => (
                                <div key={staff.summaryIdentifier || staff.staffIdentifier} className={styles.staffSummaryRow}>
                                    <div>
                                        <strong>{staff.staffName}</strong>
                                        <span>
                                            Present {Number(staff.presentDays || 0).toFixed(1)}d • LWP{' '}
                                            {Number(staff.lwpDays || 0).toFixed(1)}d • Late {Number(staff.lateCount || 0)}
                                        </span>
                                    </div>
                                    <div className={styles.staffSummaryAmount}>
                                        <strong>₹{formatINR(staff.netPay)}</strong>
                                        <span>Gross ₹{formatINR(staff.grossEarnings)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {(displayResult?.excludedStaff || []).length > 0 ? (
                        <div className={styles.resultSection}>
                            <div className={styles.resultSectionHead}>
                                <h3>Excluded by Engine</h3>
                                <span>{displayResult.excludedStaff.length} entries</span>
                            </div>
                            <div className={styles.resultList}>
                                {displayResult.excludedStaff.map((staff) => (
                                    <div key={staff.staffIdentifier} className={styles.excludedResultRow}>
                                        <div>
                                            <strong>{staff.staffName}</strong>
                                            <span>{staff.inclusionStatus}</span>
                                        </div>
                                        <p>{staff.reason}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {resultWarnings.length > 0 ? (
                        <div className={styles.resultSection}>
                            <div className={styles.resultSectionHead}>
                                <h3>Warnings in Run</h3>
                                <span>{resultWarnings.length} warning(s)</span>
                            </div>
                            <div className={styles.resultList}>
                                {resultWarnings.map((warning, index) => (
                                    <div
                                        key={`${warning?.staffIdentifier || index}-${warning?.type || 'warning-result'}`}
                                        className={styles.resultWarningRow}
                                    >
                                        <strong>{warning?.staffName || warning?.type || 'Warning'}</strong>
                                        <span>{warning?.message}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </section>
            ) : null}

            <StaffPickerModal
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={handlePickStaff}
            />
        </div>
    )
}

export default GeneratePayrollScreen